import { Plus } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { CenterFlex, Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import List from '@/components/List/List';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import TicketLabel from '@/features/Commons/Ticket/TicketLabel';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import useSingleData from '@/hooks/pure/useSingleData';
import service from '@/services/index';
import { useToast } from '@/stores/pure/ToastStore';
import { radius } from '@/styles/sizes';
import { customScrollbar } from '@/styles/style';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { track } from '@/utils/track/track';
import { timeOut } from '@/utils/util';

import TaxonomyContext from '../../../../context/TaxonomyContext';
import S from '../styles';
import type { CreateTopicModalProps } from '../types';
import UpperLevelDisplay from '../UpperLevelDisplay/UpperLevelDisplay';
import RuleTopicCandidateItem from './RuleTopicCandidateItem';

const defaultCandidateItem: Topic2CreateParams<TOPIC_2_TYPE.RULE> = {
  name: '',
  category: TOPIC_2_TYPE.RULE,
  filter_rule: {},
};

const CreateRuleTopicContainer = <T extends CreateTopicTarget>(
  props: CreateTopicModalProps<T> & {
    closeModal: () => void;
    initialCsvData?: Topic2CreateParams<TOPIC_2_TYPE.RULE>[] | null;
  },
) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const addToast = useToast();
  const { taxonomyAlias } = useContext(TaxonomyContext);
  if (!taxonomyAlias) throw new Error('Taxionomy alias is not exist');

  const [submitLoading, setSubmitLoading] = useState(false);
  // CSV 데이터가 있으면 해당 데이터로 초기화
  const [candidateList, setCandidateList] = useState<
    Topic2CreateParams<TOPIC_2_TYPE.RULE>[]
  >(
    props.initialCsvData && props.initialCsvData.length > 0
      ? props.initialCsvData
      : [{ ...defaultCandidateItem }],
  );

  const [currentPreviewItem, setCurrentPreviewItem] = useState<
    (Topic2CreateParams<TOPIC_2_TYPE.RULE> & { index: number }) | null
  >(null);

  // CSV 데이터가 있으면 첫 번째 아이템 자동 preview trigger
  useEffect(() => {
    if (props.initialCsvData && props.initialCsvData.length > 0) {
      setCurrentPreviewItem({
        ...props.initialCsvData[0],
        index: 0,
      });
    }
  }, []);

  const fetchPreview = useCallback(async () => {
    if (!currentPreviewItem) return null;
    let filterRule = { ...currentPreviewItem.filter_rule };

    const baseParams: Topic2CreateParams<TOPIC_2_TYPE.RULE> = {
      name: currentPreviewItem.name,
      category: TOPIC_2_TYPE.RULE,
      filter_rule: filterRule,
    };

    let params:
      | GetTopicPreviewParams<TOPIC_2_TYPE.RULE, 'taxonomy'>
      | GetTopicPreviewParams<TOPIC_2_TYPE.RULE, 'topic'>;

    if (props.target === 'taxonomy') {
      params = {
        ...baseParams,
        taxonomy_id: props.targetAlias,
      } as GetTopicPreviewParams<TOPIC_2_TYPE.RULE, 'taxonomy'>;
    } else {
      params = {
        ...baseParams,
        taxonomy_id: props.taxonomyAlias,
        parent_topic_id: props.targetAlias,
      } as GetTopicPreviewParams<TOPIC_2_TYPE.RULE, 'topic'>;
    }
    const res = await service.taxonomy.fetchTopicPreviewFeedback(params);
    return res;
  }, [
    currentPreviewItem,
    props.target,
    props.targetAlias,
    props.taxonomyAlias,
  ]);

  const { data: previewData, loading: previewLoading } =
    useSingleData(fetchPreview);

  const submitReq = useMemo(() => {
    switch (props.target) {
      case 'taxonomy':
        return service.taxonomy.createTopicUnderTaxonomy;
      case 'topic':
        return service.taxonomy.createTopicUnderTopic;
      default:
        throw new Error('Invalid create topic target');
    }
  }, [props.target]);

  const onSubmit = async () => {
    try {
      track(AMP_KEYS.TAXONOMY_CREATE_TOPIC_DONE, {
        type: TOPIC_2_TYPE.RULE,
      });
      setSubmitLoading(true);
      await submitReq(props.targetAlias, candidateList);
      await timeOut(2000);
      addToast('Successfully create a topic', 'success');
      props.closeModal();
    } catch (e) {
      addToast('Fail to create topics', 'fail');
    } finally {
      setSubmitLoading(false);
    }
  };

  const onAddMoreClick = async () => {
    setCandidateList((prev) => {
      const newArr = [...prev];
      newArr.push(defaultCandidateItem);
      return newArr;
    });
    await timeOut(100);
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTo({
      top: bodyRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  const isValidate = useMemo(() => {
    const everyNameFilled = candidateList.every((item) => {
      return item.name.trim() !== '';
    });
    if (!everyNameFilled)
      return {
        valid: false,
        message: 'Please fill in the topic name',
      };

    const everyCandidateHasRule = candidateList.every((item) => {
      const filter = new SFilter(item.filter_rule || {});
      const excludeLen = filter.getExcludeTextFilter()?.values.length || 0;
      const includeLen = filter.getIncludeTextFilter()?.values.length || 0;
      const isEmptyDataFilter = filter.isEmptyDataFilter();

      return excludeLen > 0 || includeLen > 0 || !isEmptyDataFilter;
    });

    if (!everyCandidateHasRule)
      return {
        valid: false,
        message: 'Set up rules to create topic(s).',
      };
    return {
      valid: true,
      message: '',
    };
  }, [JSON.stringify(candidateList)]);

  return (
    <Style g={16}>
      <S.MainLayout style={{ width: 778, overflow: 'auto' }}>
        <VFlex className="header">
          <Heading type="h2">Add rule topics</Heading>
          <Typo type="body2" weight="m">
            Up to 100 themes
            <br />
            Choose from below suggestions or add your own.
          </Typo>
        </VFlex>
        <Divider />
        <BodyStyle className="body" g={24} ref={bodyRef}>
          <UpperLevelDisplay
            target={props.target}
            targetTopicAlias={props.targetAlias}
            taxonomyAlias={props.taxonomyAlias}
          />
          <VFlex g={16}>
            {candidateList.map((item, index) => (
              <RuleTopicCandidateItem
                key={index}
                index={index}
                focused={currentPreviewItem?.index === index}
                {...item}
                onDeleteClick={() => {
                  setCandidateList((prev) => {
                    const newArr = [...prev];
                    newArr.splice(index, 1);
                    return newArr;
                  });
                }}
                onRuleChange={(index, rule) => {
                  const newItem = {
                    ...item,
                    filter_rule: rule,
                  };
                  setCandidateList((prev) => {
                    const newArr = [...prev];
                    newArr.splice(index, 1, newItem);
                    return newArr;
                  });
                }}
                onTitleChange={(index, name) => {
                  const newItem = {
                    ...item,
                    name,
                  };
                  setCandidateList((prev) => {
                    const newArr = [...prev];
                    newArr.splice(index, 1, newItem);
                    return newArr;
                  });
                  return name;
                }}
                onSeePreviewClick={() =>
                  setCurrentPreviewItem({
                    ...item,
                    index,
                  })
                }
              />
            ))}
          </VFlex>

          <Flex j="flex-start">
            <Button
              variant="ghost"
              leftIcon={<Plus />}
              sizeType="l"
              onClick={onAddMoreClick}
            >
              Add more
            </Button>
          </Flex>
        </BodyStyle>
        <Divider />
        <Flex className="footer" j="flex-end">
          <Button
            disabled={submitLoading}
            variant="ghost"
            sizeType="l"
            onClick={props.closeModal}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            sizeType="l"
            disabled={!isValidate.valid}
            onClick={onSubmit}
            isLoading={submitLoading}
            tooltip={isValidate.valid ? null : isValidate.message}
          >
            Done
          </Button>
        </Flex>
      </S.MainLayout>
      <S.PreviewLayout>
        <Heading type="h2">Preview</Heading>
        <Space h={40} />
        <VFlex f={1} g={32} style={{ overflow: 'auto' }}>
          <Typo type="label0" weight="sb">
            {currentPreviewItem?.name || 'Untitled'}
          </Typo>
          {currentPreviewItem ? (
            <VFlex g={16} f={1}>
              <Flex j="space-between">
                <Typo type="label2" weight="sb">
                  Feedback example
                </Typo>
                <Typo type="label3" weight="r" className="text-gray-600">
                  Total {previewData?.total || 0}
                </Typo>
              </Flex>
              <List
                className="gap-2 flex-1 pb-6"
                isLoading={previewLoading}
                data={previewData?.feedbacks || []}
                render={(f) => {
                  return (
                    <ExampleStyle key={f.alias}>
                      <TicketLabel type="feedback">{f.tid}</TicketLabel>
                      <Typo type="body2">{f.title}</Typo>
                    </ExampleStyle>
                  );
                }}
              />
            </VFlex>
          ) : (
            <CenterFlex f={1}>
              <Typo className="text-gray-600">
                Tab one of the AI suggested items to see the details
              </Typo>
            </CenterFlex>
          )}
        </VFlex>
      </S.PreviewLayout>
    </Style>
  );
};

const Style = styled(Flex)`
  height: calc(100vh - 48px);
`;

const BodyStyle = styled(VFlex)`
  ${customScrollbar};
  overflow: auto;
`;

const ExampleStyle = styled(Flex)`
  padding: 12px;
  background-color: ${(props) => props.theme.colors.gray100};
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-radius: ${radius.token.lg}px;
`;

export default CreateRuleTopicContainer;
